import React, { useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { X, ArrowDownCircle, ArrowUpCircle, CreditCard } from 'react-bootstrap-icons';
import { createCajaMovement } from '../../../../../../../../crud/payments_api/caja_movements';
import { toast } from 'react-toastify';
import { formatCurrency } from '../../../../../../../../utils/functions';
import './NewMovement.scss';

interface RenderModalProps {
  caja: any;
  cajas: any[];
  setCajas: (cajas: any[]) => void;
  show: boolean;
  setShow: (show: boolean) => void;
  onClose?: () => void;
  setActioncaja?: (closing: boolean) => void;
}

interface MovementType {
  id: number;
  name: string;
  description: string;
  is_positive: boolean;
  className: string;
  icon: React.ReactElement;
}

const NewMovement: React.FC<RenderModalProps> = ({ caja, cajas, setCajas, show, setShow }) => {
  const [selectedMovementType, setSelectedMovementType] = useState<MovementType | null>(null);
  const [amount, setAmount] = useState<number | null>(null);
  const [description, setDescription] = useState('');

  const availableAmount = Number(caja.current_amount);

  // Define the three movement types as requested
  const movementTypes: MovementType[] = [
    {
      id: 4, // Retiro (withdrawal)
      name: 'Retiro',
      description: 'Extracción de dinero de la caja para gastos o transferencias',
      is_positive: false,
      className: 'retiro',
      icon: <ArrowDownCircle />
    },
    {
      id: 5, // Abono (deposit)
      name: 'Abono',
      description: 'Ingreso adicional de dinero a la caja',
      is_positive: true,
      className: 'abono',
      icon: <ArrowUpCircle />
    },
    {
      id: 3, // Pago Proveedor (supplier payment)
      name: 'Pago Proveedor',
      description: 'Pago realizado directamente desde la caja a un proveedor',
      is_positive: false,
      className: 'pago',
      icon: <CreditCard />
    }
  ];

  const isExceeding =
    selectedMovementType &&
    !selectedMovementType.is_positive &&
    amount !== null &&
    amount > availableAmount;

  const isDescriptionRequired = selectedMovementType && (selectedMovementType.id === 4 || selectedMovementType.id === 3 || selectedMovementType.id === 5); // Retiro or Pago Proveedor or Abono
  const isDescriptionMissing = isDescriptionRequired && description.trim() === '';
  const isDescriptionTooShort = isDescriptionRequired && description.trim().length > 0 && description.trim().length < 15;

  const isRegistrarDisabled = Boolean(
    !selectedMovementType || 
    amount === null || 
    (amount !== null && amount <= 0) || 
    isExceeding ||
    isDescriptionMissing ||
    isDescriptionTooShort
  );

  const handleCajaMovement = () => {
    if (!selectedMovementType) {
      toast.error('Debe seleccionar un tipo de movimiento');
      return;
    }
    if (!amount || amount <= 0) {
      toast.error('Debe ingresar un monto válido mayor a 0');
      return;
    }
    if (isExceeding) {
      toast.error(
        `El monto ingresado excede el efectivo en caja: ${formatCurrency(availableAmount)}`
      );
      return;
    }
    if (isDescriptionMissing) {
      toast.error('La descripción es obligatoria para este tipo de movimiento');
      return;
    }
    if (isDescriptionTooShort) {
      toast.error('La descripción debe tener al menos 15 caracteres');
      return;
    }

    const user = JSON.parse(localStorage.getItem('user') ?? '{}');
    const userId = user ? Number(user.user_id) : 0;
    const movementTypeId = selectedMovementType.id;
    const movementDescription = description;
    const movementAmount = amount;

    createCajaMovement(caja.workstation_id, userId, movementTypeId, movementAmount, movementDescription)
      .then((response) => {
        toast.success(`Movimiento de caja registrado con éxito`);
        setShow(false);
        
        // Update caja amount based on movement type
        setCajas(cajas.map((c: any) => c.workstation_id === caja.workstation_id ? {
          ...c,
          current_amount: c.current_amount + (selectedMovementType.is_positive ? movementAmount : -movementAmount)
        } : c));

        // Reset form
        setSelectedMovementType(null);
        setAmount(null);
        setDescription('');
      })
      .catch((error) => {
        console.error('Error creating caja movement: ', error);
        toast.error('Error registrando movimiento de caja');
      });
  };

  const handleClose = () => {
    setShow(false);
    setSelectedMovementType(null);
    setAmount(null);
    setDescription('');
  };

  return (
    <Modal show={show} onHide={handleClose} className="caja-movement-modal" centered>
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">Registrar Movimiento</Modal.Title>
        </div>
        <Button variant="none" onClick={handleClose}>
          <X size={24} />
        </Button>
      </Modal.Header>
      <Modal.Body>
        {/* Movement Type Selection */}
        <div className="movement-type-section">
          <div className="section-label">Tipo de movimiento</div>
          <div className="movement-options">
            {movementTypes.map((movement) => (
              <div
                key={movement.id}
                className={`movement-option ${selectedMovementType?.id === movement.id ? 'selected' : ''}`}
                onClick={() => setSelectedMovementType(movement)}
              >
                <div className={`movement-icon ${movement.className}`}>
                  {movement.icon}
                </div>
                <div className="movement-content">
                  <div className="movement-title">
                    {movement.name}
                  </div>
                  <div className="movement-description">
                    {movement.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Amount Input */}
        <div className="amount-input-section">
          <div className="field-label">Monto</div>
          <div className={`amount-input-group ${isExceeding ? 'error' : ''}`}>
            <div className="currency-symbol">$</div>
            <input
              type="text"
              className="amount-input"
              value={
                amount !== null
                  ? amount.toLocaleString('es-CR', {
                      maximumFractionDigits: 0,
                    })
                  : ''
              }
              onChange={(e) => {
                const onlyNums = e.target.value.replace(/\D/g, '');
                setAmount(onlyNums ? Number(onlyNums) : null);
              }}
              placeholder="0"
            />
          </div>
          {isExceeding && (
            <div className="error-message">
              El monto ingresado no puede ser mayor que el efectivo en caja: {formatCurrency(availableAmount)}
            </div>
          )}
        </div>

        {/* Description Input */}
        <div className="description-section">
          <div className="field-label">
            Descripción
            {isDescriptionRequired && <span className="required-indicator">*</span>}
          </div>
          <textarea
            className={`description-input ${isDescriptionMissing || isDescriptionTooShort ? 'required' : ''}`}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={
              isDescriptionRequired 
                ? "Describa el motivo del movimiento (mínimo 15 caracteres)"
                : "Describa el motivo del movimiento (opcional)"
            }
            minLength={isDescriptionRequired ? 15 : undefined}
          />
          {isDescriptionMissing && (
            <div className="required-message">
              La descripción es obligatoria para este tipo de movimiento
            </div>
          )}
          {isDescriptionTooShort && (
            <div className="required-message">
              La descripción debe tener al menos 15 caracteres
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button variant="none" className="form-button-cancel" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            variant="none"
            className="form-button-create"
            onClick={handleCajaMovement}
            disabled={isRegistrarDisabled}
          >
            Registrar
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default NewMovement;